import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getVendorById, uploadVendorDocument, getVendorDocuments, deleteVendorDocument, downloadVendorDocument, viewVendorDocument } from "../../services/api/boq/VendorService";
import { ArrowLeft } from "lucide-react";
import toast from "react-hot-toast";
import { FaDownload, FaTrash, FaEye } from "react-icons/fa";

export default function VendorView() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [vendor, setVendor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [documents, setDocuments] = useState([]);
    const [uploadModal, setUploadModal] = useState(false);
    const [file, setFile] = useState(null);
    const [deleteId, setDeleteId] = useState(null);
    const [deleteModal, setDeleteModal] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploading, setUploading] = useState(false);

    const fetchVendor = async () => {
        try {
            const res = await getVendorById(id);
            setVendor(res.data?.data || res.data);
        } catch (err) {
            toast.error("Failed to load vendor");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDocuments();
        fetchVendor();
    }, []);

    const isValidFileName = (name) => {
        const regex = /^[a-z0-9_]+\.(pdf|jpg|jpeg|png)$/i;
        return regex.test(name);
    };
    const handleUpload = async (e) => {
        e.preventDefault();

        if (!file) {
            toast.error("Please select a file");
            return;
        }

        const fileName = file.name;

        // filename validation
        if (!isValidFileName(fileName)) {
            toast.error(
                "Invalid filename. Use only letters, numbers, underscore (_). Example: gst_certificate.pdf"
            );
            return;
        }

        try {
            setUploading(true);
            setUploadProgress(0);

            const res = await uploadVendorDocument(
                id,
                file,
                (progress) => setUploadProgress(progress)
            );

            const newDoc = {
                id: res.data?.id || Date.now(),
                name: fileName,
                file: res.data?.data?.file_url || URL.createObjectURL(file),
            };

            fetchDocuments();

            toast.success("Document uploaded successfully");

            setUploadModal(false);
            setFile(null);

        } catch (error) {
            console.error(error);
            toast.error("Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const fetchDocuments = async () => {
        try {
            const res = await getVendorDocuments(id);
            setDocuments(res.data?.documents || []);
        } catch (error) {
            toast.error("Failed to load documents");
        }
    };

    const deleteDocument = (id) => {
        setDocuments(documents.filter((d) => d.id !== id));
    };

    const confirmDelete = async () => {
        try {
            await deleteVendorDocument(deleteId);
            toast.success("Document deleted");

            fetchDocuments();
            setDeleteModal(false);

        } catch (error) {
            toast.error("Delete failed");
        }
    };

    const handleView = async (docId) => {
        try {
            const response = await viewVendorDocument(docId);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            window.open(url, "_blank");
        } catch (error) {
            toast.error("Failed to open document");
        }
    };
    const handleDownload = async (doc) => {
        try {
            const response = await downloadVendorDocument(doc.id);

            const disposition = response.headers["content-disposition"];

            let filename = doc.original_name;

            if (disposition && disposition.includes("filename=")) {
                filename = disposition
                    .split("filename=")[1]
                    .replace(/"/g, "");
            }

            const url = window.URL.createObjectURL(response.data);

            const link = document.createElement("a");
            link.href = url;
            link.download = filename;

            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

        } catch (error) {
            toast.error("Download failed");
        }
    };

    if (loading)
        return <div className="p-6 text-center text-gray-500">Loading...</div>;

    if (!vendor)
        return <div className="p-6 text-center text-red-500">Vendor not found</div>;

    return (
        <div className="p-4 md:p-6">

            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <button
                    onClick={() => navigate("/vendors")}
                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-black"
                >
                    <ArrowLeft size={18} />
                    Back
                </button>

                <h2 className="text-xl font-semibold">Vendor Details</h2>
            </div>

            {/* Card */}
            <div className="bg-white rounded-xl shadow-sm border p-6 max-w-full">

                <div className="grid md:grid-cols-2 gap-6">

                    <Field label="Vendor Name" value={vendor.name} />

                    <Field label="Email" value={vendor.email} />

                    <Field label="Mobile" value={vendor.mobile || "-"} />

                    <Field label="GST Number" value={vendor.gst_number || "-"} />

                    <Field label="Status"
                        value={vendor.status === 1 || vendor.status === "active"
                            ? "Active"
                            : "Inactive"}
                    />

                    <div className="md:col-span-2">
                        <Field label="Address" value={vendor.address || "-"} />
                    </div>

                </div>

            </div>

            {/* Upload Document */}
            <div className="flex justify-between items-center mt-6">

                <h3 className="text-lg font-semibold">Vendor Documents</h3>

                <button
                    onClick={() => setUploadModal(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm"
                >
                    Upload Document
                </button>

            </div>

            {/* Documents Table */}
            <div className="bg-white rounded-xl shadow-sm border p-6 mt-6">

                {documents.length === 0 ? (
                    <p className="text-gray-500 text-sm">No documents uploaded</p>
                ) : (
                    <div className="overflow-x-auto">

                        <table className="w-full text-sm">

                            <thead className="bg-gray-50 border-b">
                                <tr>
                                    <th className="p-3 text-left">SL No</th>
                                    <th className="p-3 text-left">Document Name</th>
                                    <th className="p-3 text-left">File Size</th>
                                    <th className="p-3 text-center">Action</th>
                                </tr>
                            </thead>

                            <tbody>

                                {documents.map((doc, index) => (
                                    <tr key={doc.id} className="border-b">

                                        <td className="p-3">{index + 1}</td>

                                        <td className="p-3">{doc.original_name}</td>

                                        <td className="p-3">
                                            {(doc.file_size / 1024 / 1024).toFixed(2)} MB
                                        </td>

                                        <td className="p-3 flex gap-3 text-center justify-center">
                                            <button
                                                onClick={() => handleDownload(doc)}
                                                className="text-blue-600 hover:text-blue-800"
                                                title="Download"
                                            >
                                                <FaDownload size={18} />
                                            </button>

                                            <button
                                                onClick={() => handleView(doc.id)}
                                                className="text-green-600 hover:text-green-800"
                                                title="View"
                                            >
                                                <FaEye size={18} />
                                            </button>

                                            <button
                                                onClick={() => {
                                                    setDeleteId(doc.id);
                                                    setDeleteModal(true);
                                                }}
                                                className="text-red-600 hover:text-red-800"
                                                title="Delete"
                                            >
                                                <FaTrash size={18} />
                                            </button>
                                        </td>


                                    </tr>
                                ))}

                            </tbody>

                        </table>

                    </div>
                )}

            </div>

            {uploadModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">

                    <div className="bg-white w-full max-w-md rounded-xl shadow-lg">

                        {/* Header */}
                        <div className="flex justify-between items-center p-4 border-b">
                            <h3 className="font-semibold">Upload Document</h3>

                            <button
                                onClick={() => setUploadModal(false)}
                                className="text-gray-500 hover:text-black"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Form */}
                        <form onSubmit={handleUpload} className="p-5 space-y-4">

                            <div>
                                <label className="text-sm text-gray-600">Select File</label>

                                <input
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={(e) => setFile(e.target.files[0])}
                                    className="w-full border rounded-lg px-3 py-2"
                                />

                                <p className="text-xs text-gray-500 mt-1">
                                    Use lowercase letters, numbers and underscore only.
                                    Example: gst_certificate.pdf
                                </p>
                            </div>

                            {uploading && (
                                <div className="mt-2">
                                    <p className="text-xs text-gray-600 mb-1">
                                        Uploading... {uploadProgress}%
                                    </p>

                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div
                                            className="bg-blue-600 h-2 rounded-full"
                                            style={{ width: `${uploadProgress}%` }}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-end gap-3 pt-3 border-t">

                                <button
                                    type="button"
                                    onClick={() => setUploadModal(false)}
                                    className="px-4 py-2 border rounded-lg"
                                >
                                    Cancel
                                </button>

                                <button
                                    type="submit"
                                    disabled={uploading}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50"
                                >
                                    {uploading ? "Uploading..." : "Upload"}
                                </button>

                            </div>

                        </form>

                    </div>

                </div>

            )}

            {deleteModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">

                    <div className="bg-white w-full max-w-sm rounded-xl shadow-lg p-5">

                        <h3 className="text-lg font-semibold mb-3">Confirm Delete</h3>

                        <p className="text-gray-600 text-sm">
                            Are you sure you want to delete this document?
                        </p>

                        <div className="flex justify-end gap-3 mt-5">

                            <button
                                onClick={() => setDeleteModal(false)}
                                className="px-4 py-2 border rounded-lg"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={confirmDelete}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg"
                            >
                                Delete
                            </button>

                        </div>

                    </div>

                </div>
            )}
        </div>
    );
}

function Field({ label, value }) {
    return (
        <div>
            <p className="text-sm text-gray-500">{label}</p>
            <p className="mt-1 font-medium">{value}</p>
        </div>
    );
}